"use client"

import { useEffect, useMemo, useState } from "react"
import { cva } from "class-variance-authority"

import type { ReducedProductData } from "@/lib/storefront/product/product"
import type { MarketChannel } from "@/lib/market/market-channel"
import { cn } from "@/lib/utils"
import ProductCardWrapper from "@/components/product/ProductCardWrapper"
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/components"

type ProductDetailProductCarouselClientProps = {
  endpoint: "cross-selling" | "popular-in-category"
  productId: string
  title: string
  templateBlok: any
  isoLocale: string
  sbLocale: string
  channel?: MarketChannel
  applicationSettings?: ProductCarouselApplicationSettings
}

type ProductsResponse = {
  products?: ReducedProductData[]
}

type ProductCarouselApplicationSettings = {
  allowDebugMode?: boolean
  hideAddToBasketButton?: boolean
  priceRequestLink?: string
}

const productCarouselPromiseCache = new Map<string, Promise<ProductsResponse | undefined>>()

const visibleSlides = cva("", {
  variants: {
    cols: {
      "1": "basis-full",
      "2": "basis-full sm:basis-1/2",
      "3": "basis-full sm:basis-1/2 md:basis-1/3",
      "4": "basis-full sm:basis-1/2 md:basis-1/3 lg:basis-1/4",
      "5": "basis-full sm:basis-1/2 md:basis-1/3 lg:basis-1/4 xl:basis-1/5",
      "6": "basis-full sm:basis-1/2 md:basis-1/3 lg:basis-1/4 xl:basis-1/5 2xl:basis-1/6",
    },
  },
  defaultVariants: {
    cols: "6",
  },
})

function loadProducts(
  endpoint: ProductDetailProductCarouselClientProps["endpoint"],
  productId: string,
  sbLocale: string,
  channel?: MarketChannel
) {
  const cacheKey = `${endpoint}:${sbLocale}:${channel ?? ""}:${productId}`
  let promise = productCarouselPromiseCache.get(cacheKey)

  if (!promise) {
    const searchParams = new URLSearchParams()
    searchParams.set("sbLocale", sbLocale)
    if (channel) {
      searchParams.set("channel", channel)
    }

    promise = fetch(
      `/api/storefront/product-${endpoint}/${encodeURIComponent(productId)}?${searchParams.toString()}`,
      {
        cache: "no-store",
        credentials: "same-origin",
      }
    ).then((response) => (response.ok ? response.json() : undefined))
    productCarouselPromiseCache.set(cacheKey, promise)
  }

  return promise
}

export default function ProductDetailProductCarouselClient({
  endpoint,
  productId,
  title,
  templateBlok,
  isoLocale,
  sbLocale,
  channel,
  applicationSettings,
}: ProductDetailProductCarouselClientProps) {
  const [products, setProducts] = useState<ReducedProductData[]>()

  useEffect(() => {
    if (!productId) {
      return
    }

    let active = true

    loadProducts(endpoint, productId, sbLocale, channel)
      .then((result) => {
        if (active) {
          setProducts(result?.products ?? [])
        }
      })
      .catch(() => {
        if (active) {
          setProducts([])
        }
      })

    return () => {
      active = false
    }
  }, [channel, endpoint, productId, sbLocale])

  const normalizedProducts = useMemo(
    () => products?.map((product) => ({ ...product, link: product.link })) ?? [],
    [products]
  )

  if (!normalizedProducts.length) {
    return null
  }

  return (
    <div className="flex flex-col">
      <h2 className="text-2xl font-bold">{title}</h2>
      <Carousel
        className="mt-6 max-w-full px-0"
        opts={{
          align: "start",
          loop: true,
        }}
        duration={0}
        autoplay={false}
      >
        <CarouselContent className="-ml-3.5">
          {normalizedProducts.map((productData, index) => (
            <CarouselItem key={productData.id ?? index} className={cn(visibleSlides({ cols: "6" }))}>
              <ProductCardWrapper
                productData={productData}
                effectivePrices={undefined}
                view="grid"
                cardVariant={templateBlok?.variant ?? "standard"}
                isB2B={true}
                debugMode={templateBlok?.debugMode && applicationSettings?.allowDebugMode}
                hideAddToBasket={applicationSettings?.hideAddToBasketButton ?? false}
                priceRequestLink={applicationSettings?.priceRequestLink ?? ""}
                showEcoTax={templateBlok?.ecoTax}
                isoLocale={isoLocale}
                sbLocale={sbLocale}
                channel={channel}
                trackingList={endpoint === "cross-selling" ? "pdp_cross_selling" : "pdp_popular_in_category"}
                searchTerm={endpoint === "cross-selling" ? "pdp_cross_selling" : "pdp_popular_in_category"}
                position={index + 1}
                mainProductId={endpoint === "cross-selling" ? productId : undefined}
                isRecommendation={endpoint === "cross-selling" ? true : templateBlok?.isRecommendation}
                inStoryBlock={false}
              />
            </CarouselItem>
          ))}
        </CarouselContent>

        <CarouselPrevious
          className="border-2 border-primary bg-white p-0.5 text-primary sm:left-[-1.3rem]"
          bannermode={false}
          fullwidth={true}
        />

        <CarouselNext
          className="border-2 border-primary bg-white p-0.5 text-primary"
          bannermode={false}
          fullwidth={true}
        />
      </Carousel>
    </div>
  )
}
